import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/client';
import { useAuth } from './AuthContext';

const PricingContext = createContext(null);

const EMPTY = { credit_limit: 0, payment_methods: ['instant'], overrides: [] };

export const PricingProvider = ({ children }) => {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(EMPTY);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    if (!user) { setCtx(EMPTY); return; }
    try {
      const r = await api.get('/me/pricing-context');
      setCtx({
        credit_limit: r.data.credit_limit || 0,
        payment_methods: r.data.payment_methods || ['instant'],
        overrides: r.data.overrides || [],
      });
    } catch (e) {
      setCtx(EMPTY);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build lookup maps once per ctx change
  const overrideMaps = useMemo(() => {
    const product = new Map();
    const category = new Map();
    for (const o of ctx.overrides) {
      const entry = {
        discount_pct: Number(o.discount_pct || 0),
        fixed_price: (o.fixed_price !== null && o.fixed_price !== undefined) ? Number(o.fixed_price) : null,
        bulk_qty: (o.bulk_qty !== null && o.bulk_qty !== undefined) ? Number(o.bulk_qty) : null,
        bulk_price: (o.bulk_price !== null && o.bulk_price !== undefined) ? Number(o.bulk_price) : null,
      };
      if (o.type === 'product') product.set(o.identifier, entry);
      else if (o.type === 'category') category.set(o.identifier, entry);
    }
    return { product, category };
  }, [ctx.overrides]);

  // Slug -> [slug, parent_slug, ...]  memoised
  const ancestorMap = useMemo(() => {
    const bySlug = new Map(categories.map(c => [c.slug, c]));
    const byId = new Map(categories.map(c => [c.id, c]));
    const out = new Map();
    for (const c of categories) {
      const chain = [];
      let cur = c;
      const seen = new Set();
      while (cur && !seen.has(cur.slug)) {
        seen.add(cur.slug);
        chain.push(cur.slug);
        cur = cur.parent_id ? byId.get(cur.parent_id) : null;
      }
      out.set(c.slug, chain);
    }
    // ensure bySlug touched to avoid unused-var lint noise
    void bySlug;
    return out;
  }, [categories]);

  /** Returns override for the given product as { discount_pct, fixed_price }, or null. */
  const resolveOverride = useCallback((product) => {
    if (!product) return null;
    const sku = product.sku;
    if (sku && overrideMaps.product.has(sku)) return overrideMaps.product.get(sku);
    const catSlug = product.category;
    const chain = ancestorMap.get(catSlug) || (catSlug ? [catSlug] : []);
    for (const slug of chain) {
      if (overrideMaps.category.has(slug)) return overrideMaps.category.get(slug);
    }
    return null;
  }, [overrideMaps, ancestorMap]);

  const hasOverride = ctx.overrides.length > 0;

  return (
    <PricingContext.Provider
      value={{
        creditLimit: ctx.credit_limit,
        paymentMethods: ctx.payment_methods,
        overrides: ctx.overrides,
        resolveOverride,
        hasOverride,
        refresh,
        categories,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => useContext(PricingContext);
